/**
 * 사업자 활동 로그의 "변경 내용" 셀 렌더링
 * - 이벤트 코드별로 요구사항에 명시된 형태로 텍스트 조립
 */
import type { BusinessActivityLog } from '@/services/api';
import {
  TICKET_PRODUCT_LABEL,
  BIZ_ACTOR_ROLE_LABEL,
} from '@/services/api';

function won(n: number) {
  return `${n.toLocaleString()}원`;
}

/** 시스템 이벤트(actor 없음) 또는 actor 정보가 있을 때 "이름(권한)" 포맷 */
function actorSuffix(log: BusinessActivityLog): string {
  if (!log.actorName || !log.actorRole) return '시스템';
  return `${log.actorName}(${BIZ_ACTOR_ROLE_LABEL[log.actorRole]})`;
}

/**
 * productType → "(수업)" 같은 괄호 포함 라벨 반환.
 * 매핑 없거나 빈 값이면 빈 문자열 — 호출부에서 그대로 삽입해도 "()" 가 생기지 않음.
 */
function productLabelParen(
  k: keyof typeof TICKET_PRODUCT_LABEL | string | null | undefined,
): string {
  if (!k) return '';
  const label = TICKET_PRODUCT_LABEL[k as keyof typeof TICKET_PRODUCT_LABEL];
  return label ? `(${label})` : '';
}

export function formatBusinessLog(log: BusinessActivityLog): JSX.Element {
  const productLabel = productLabelParen;

  switch (log.eventCode) {
    case 'TICKET_ISSUE': {
      const p = log.payload;
      const qty = p.quantity ?? 1;
      // "3장 × 8회권" 같은 발급도 표기. 1장은 매수 생략.
      const quantityLabel = qty > 1 ? `수량 ${qty}장, ` : '';
      return (
        <span>
          <strong>[{p.ticketName}]</strong>이용권{productLabel(p.productType)}
          {' '}발급 → {p.petName}({p.guardianName}), {quantityLabel}1장당 {p.count}회
        </span>
      );
    }
    case 'TICKET_RECALL': {
      const p = log.payload;
      return (
        <span>
          <strong>[{p.ticketName}]</strong>이용권{productLabel(p.productType)}
          {' '}회수 → {p.petName}({p.guardianName}), 잔여횟수 {p.remainCount}개
        </span>
      );
    }
    case 'TICKET_REGISTER': {
      const p = log.payload;
      return (
        <span>
          <strong>[{p.ticketName}]</strong>{productLabel(p.productType)} 등록
          {' '}(금액 {won(p.price)}, 할인율 {p.discountRate}%, 횟수 {p.count}개)
        </span>
      );
    }
    case 'TICKET_DELETE': {
      const p = log.payload;
      return (
        <span>
          <strong>[{p.ticketName}]</strong>{productLabel(p.productType)} 삭제
          {' '}- {actorSuffix(log)}
        </span>
      );
    }
    case 'RESERV_REQUEST': {
      const p = log.payload;
      return (
        <span>
          {p.petName}({p.guardianName}) 예약 요청 ({p.ticketName})
        </span>
      );
    }
    case 'RESERV_APPROVE': {
      const p = log.payload;
      return (
        <span>
          {p.petName}({p.guardianName}) 예약 승인 ({p.ticketName})
        </span>
      );
    }
    case 'RESERV_CANCEL': {
      const p = log.payload;
      return (
        <span>
          {p.petName}({p.guardianName}) 예약 취소 ({p.ticketName})
        </span>
      );
    }
    case 'ACCOUNT_ADD': {
      const p = log.payload;
      return (
        <span>
          계정 추가 - {p.targetAccountName}({BIZ_ACTOR_ROLE_LABEL[p.targetRole]})
        </span>
      );
    }
    case 'ACCOUNT_REMOVE': {
      const p = log.payload;
      return (
        <span>
          계정 제거 - {p.targetAccountName}({BIZ_ACTOR_ROLE_LABEL[p.targetRole]})
        </span>
      );
    }
    case 'PROFILE_PHOTO':
      return (
        <span>
          프로필 사진 등록 - {actorSuffix(log)}
        </span>
      );
    case 'NOTICE_SEND': {
      const p = log.payload;
      return (
        <span>
          알림장 발송 - {actorSuffix(log)}, {p.sentCount}건
        </span>
      );
    }
    case 'NOTICE_RECEIVE': {
      const p = log.payload;
      return (
        <span>
          알림장 수신 - {p.fromGuardianName}({p.petName})
        </span>
      );
    }
    case 'PAYMENT_SUCCESS': {
      const p = log.payload;
      return (
        <span>
          결제 성공 - <strong>[{p.productName}]</strong> {won(p.amount)}
        </span>
      );
    }
    case 'PAYMENT_FAIL': {
      const p = log.payload;
      return (
        <span className="text-red-600">
          결제 실패 - <strong>[{p.productName}]</strong> {won(p.amount)}
        </span>
      );
    }
  }
}
